/**
 * Client-side image optimization, on for every seller (see AuthContext). Image
 * uploads are downscaled and re-encoded in the browser BEFORE they reach the
 * server/R2 — no Cloudflare Images, no server CPU. This shrinks the multi-
 * megabyte PNGs and phone photos sellers paste, which are far too heavy for the
 * web: the bucket had 795MB of them against 4MB of WebP.
 *
 * Which output format wins depends on the engine, and the two disagree in
 * opposite directions — see plannedEncoders.
 *
 * The decision is deliberately fail-safe: any decode/encode problem, an unknown
 * browser, an animated GIF, an SVG, or a result that isn't actually smaller all
 * fall through to the ORIGINAL file untouched — optimization must never corrupt
 * an upload or make it worse.
 */

export interface ImageOptimizeConfig {
  /** Re-encode raster uploads at all. When false, nothing is touched. (The name
   *  predates AVIF joining WebP as a possible output; the stored setting is
   *  `optimize_images_webp`, so renaming it would mean a migration for nothing.) */
  webp: boolean;
  /** Also downscale oversized images and use a tighter quality. When false, the
   *  original dimensions are kept at near-lossless quality (format change only). */
  compress: boolean;
}

// Module-level config, set once identity is known (null = feature off / not a
// platform admin). Read by maybeOptimizeImageFormData on every image upload.
let config: ImageOptimizeConfig | null = null;

export function setImageOptimizeConfig(next: ImageOptimizeConfig | null): void {
  config = next;
}

export function getImageOptimizeConfig(): ImageOptimizeConfig | null {
  return config;
}

// Longest edge when compressing. 1600px covers full-width hero images on
// retina displays; storefront thumbnails are far smaller.
const MAX_EDGE = 1600;
const QUALITY_COMPRESS = 0.8;
const QUALITY_LOSSLESSISH = 0.95;

// Raster types worth re-encoding. GIF is excluded (canvas would flatten any
// animation); SVG is vector (nothing to gain, would rasterize).
//
// WebP and AVIF are IN. They need no format change, but a 2400px one still has
// to meet the ceiling and used to sail past untouched — and since WebKit now
// PRODUCES AVIF here, leaving AVIF out would mean an iPhone re-upload of an
// already-optimized image skips the ceiling. Measured: a 2400px AVIF from
// WebKit is 5.1MB, the same image at 1600px is 1.95MB.
const MODERN = new Set(["image/webp", "image/avif"]);
const CONVERTIBLE = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  ...MODERN,
]);

const EXT_FOR: Record<string, string> = {
  "image/webp": "webp",
  "image/avif": "avif",
  "image/jpeg": "jpg",
};

/**
 * Which encoders to try, in order. Measured with canvas.toBlob in real engines:
 * Chromium encodes WebP but NOT AVIF; WebKit is the exact opposite — it hands
 * back a PNG for image/webp and encodes AVIF happily. A single-format attempt
 * therefore means no optimization at all on one of the two engines, which is
 * the worst case here: the phone camera uploads are the heavy ones.
 *
 * JPEG is last and only offered when the image is being downscaled. Re-encoding
 * a JPEG that keeps its dimensions would trade real quality for a few bytes —
 * the caller's own size check cannot tell that apart from a genuine win.
 */
export function plannedEncoders(willDownscale: boolean): string[] {
  return willDownscale
    ? ["image/webp", "image/avif", "image/jpeg"]
    : ["image/webp", "image/avif"];
}

/** `poster.png` + image/avif → `poster.avif`. */
export function outputNameFor(name: string, mime: string): string {
  return `${name.replace(/\.[^.]+$/, "")}.${EXT_FOR[mime] ?? "webp"}`;
}

/**
 * Re-encode one image File per `config`, trying each encoder the engine may
 * support. Returns the original File unchanged whenever conversion is disabled,
 * not applicable, or not beneficial.
 */
export async function maybeOptimizeImageFile(file: File): Promise<File> {
  const cfg = config;
  if (!cfg || !cfg.webp) return file;
  if (!CONVERTIBLE.has(file.type)) return file;
  if (typeof document === "undefined" || typeof createImageBitmap !== "function") return file;

  try {
    const bitmap = await createImageBitmap(file);
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = cfg.compress && longest > MAX_EDGE ? MAX_EDGE / longest : 1;
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close?.();
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const quality = cfg.compress ? QUALITY_COMPRESS : QUALITY_LOSSLESSISH;
    // A WebP/AVIF that already fits the ceiling has nothing left to gain from a
    // format-preserving re-encode, so leave it exactly as the seller sent it.
    if (MODERN.has(file.type) && scale === 1) return file;

    for (const mime of plannedEncoders(scale < 1)) {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, mime, quality),
      );
      // An engine that cannot encode this format hands back a PNG under its own
      // mime. Checking the type is what stops a mislabeled upload, and it is
      // also how we learn to move on to the next encoder.
      if (!blob || blob.type !== mime) continue;
      if (blob.size >= file.size) continue;
      return new File([blob], outputNameFor(file.name, mime), {
        type: mime,
        lastModified: file.lastModified,
      });
    }
    return file;
  } catch {
    return file;
  }
}

/**
 * Wrap an image-upload FormData: convert the `file` entry to WebP when enabled.
 * Only the `file` field is touched — multi-file delivery uploads use `files`
 * (plural) and must never be re-encoded. Returns the same FormData (mutated) or
 * a new one carrying the converted file.
 */
export async function maybeOptimizeImageFormData(
  fd: FormData,
  field = "file",
): Promise<FormData> {
  if (!config?.webp) return fd;
  const entry = fd.get(field);
  if (!(entry instanceof File)) return fd;
  const optimized = await maybeOptimizeImageFile(entry);
  if (optimized === entry) return fd;
  fd.set(field, optimized, optimized.name);
  return fd;
}
